import { useLayoutEffect, useRef, useState, useId, type ChangeEvent, type InputHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

export function parseDateDraft(raw: string, min?: string, max?: string): string | null {
  if (!raw) return '';
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  if (!match || Number(match[3]) < 1) return null;
  const iso = `${match[3]}-${match[2]}-${match[1]}`;
  const date = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso || (min && iso < min.slice(0, 10)) || (max && iso > max.slice(0, 10))) return null;
  return iso;
}

export function dateInputText(value: string | null | undefined): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

export function validateDateInputs(root: ParentNode = document): boolean {
  const invalid = Array.from(root.querySelectorAll<HTMLInputElement>('input[data-date-input]')).find((input) => !input.disabled && !input.checkValidity());
  if (!invalid) return true;
  invalid.reportValidity();
  invalid.focus();
  return false;
}

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'defaultValue' | 'min' | 'max'> & { value?: string | null; min?: string; max?: string; onDraftChange?: (raw: string) => void };

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
function todayDate() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function DateInput({ value = '', min, max, onChange, onBlur, onDraftChange, ...props }: Props) {
  const [draft, setDraft] = useState(() => dateInputText(value));
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => (value || todayDate()).slice(0, 7));
  const [position, setPosition] = useState({ left: 8, top: 8, width: 280, maxHeight: 320 });
  const committed = useRef(value ?? '');
  const pending = useRef<ChangeEvent<HTMLInputElement> | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const picker = useRef<HTMLDivElement>(null);
  const pickerId = useId();
  function commit(iso: string, event: ChangeEvent<HTMLInputElement>) {
    committed.current = iso;
    pending.current = null;
    onChange?.({ ...event, target: { ...event.target, value: iso }, currentTarget: { ...input.current, value: iso } } as ChangeEvent<HTMLInputElement>);
  }
  function close(focus = false) {
    setOpen(false);
    if (focus) input.current?.focus();
  }
  function choose(iso: string) {
    const raw = dateInputText(iso);
    if (props.disabled || props.readOnly || parseDateDraft(raw, min, max) === null || !input.current) return;
    setDraft(raw);
    pending.current = null;
    input.current.setCustomValidity('');
    if (iso !== committed.current.slice(0, 10)) {
      onDraftChange?.(raw);
      commit(iso, { target: input.current, currentTarget: input.current } as ChangeEvent<HTMLInputElement>);
    }
    close(true);
  }
  useLayoutEffect(() => {
    if ((value ?? '') !== committed.current) {
      committed.current = value ?? '';
      if (!pending.current) setDraft(dateInputText(value));
    }
  }, [value]);
  useLayoutEffect(() => {
    const iso = parseDateDraft(draft, min, max);
    input.current?.setCustomValidity(iso === null ? 'Enter a real date as DD/MM/YYYY within the allowed range.' : '');
    if (iso !== null && pending.current) commit(iso, pending.current);
  }, [draft, min, max]);
  useLayoutEffect(() => {
    if (!open) return;
    if (props.disabled || props.readOnly) { close(); return; }
    function place() {
      const rect = input.current!.getBoundingClientRect();
      const width = Math.min(280, window.innerWidth - 16);
      const height = Math.min(320, window.innerHeight - 16);
      const top = rect.bottom + 4 + height <= window.innerHeight - 8 ? rect.bottom + 4 : Math.max(8, rect.top - height - 4);
      setPosition({ left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), top, width, maxHeight: window.innerHeight - top - 8 });
    }
    function outside(event: PointerEvent) {
      const target = event.target as Node;
      if (!picker.current?.contains(target) && !trigger.current?.contains(target) && target !== input.current) close();
    }
    function escape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopImmediatePropagation(); close(true);
    }
    place();
    picker.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]:not(:disabled), [data-calendar-day]:not(:disabled)')?.focus();
    document.addEventListener('pointerdown', outside);
    window.addEventListener('keydown', escape, true);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      window.removeEventListener('keydown', escape, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, props.disabled, props.readOnly]);
  const year = Number(month.slice(0, 4)), monthIndex = Number(month.slice(5, 7)) - 1;
  const first = new Date(`${month}-01T00:00:00.000Z`);
  const last = new Date(first); last.setUTCMonth(monthIndex + 1); last.setUTCDate(0);
  const selected = parseDateDraft(draft, min, max);
  const allowed = (iso: string) => parseDateDraft(dateInputText(iso), min, max) !== null;
  function navigateMonth(offset: number) {
    const date = new Date(first); date.setUTCMonth(monthIndex + offset);
    if (date.getUTCFullYear() >= 1 && date.getUTCFullYear() <= 9999) setMonth(date.toISOString().slice(0, 7));
  }
  return <span style={{ display: 'inline-grid', gridTemplateColumns: 'minmax(0, 1fr) 32px', alignItems: 'center', maxWidth: '100%', width: props.className?.split(/\s+/).includes('w-full') ? '100%' : undefined }}>
    <input {...props} ref={input} type="text" inputMode="numeric" placeholder="DD/MM/YYYY" data-date-input="true" value={draft} maxLength={10} style={{ minWidth: 0, width: '100%', ...props.style }} onChange={(event) => {
      const raw = event.target.value;
      setDraft(raw);
      onDraftChange?.(raw);
      const iso = parseDateDraft(raw, min, max);
      event.target.setCustomValidity(iso === null ? 'Enter a real date as DD/MM/YYYY within the allowed range.' : '');
      if (iso === null) { pending.current = event; return; }
      commit(iso, event);
    }} onBlur={onBlur} />
    <button ref={trigger} type="button" aria-label={`Open calendar${props['aria-label'] ? ` for ${props['aria-label']}` : ''}`} aria-expanded={open} aria-controls={open ? pickerId : undefined} aria-haspopup="dialog" disabled={props.disabled || props.readOnly} className="inline-flex items-center justify-center text-muted hover:text-white disabled:opacity-40" style={{ width: 32, height: 32 }} onClick={() => {
      if (open) { close(true); return; }
      const date = parseDateDraft(draft, min, max) || value?.slice(0, 10) || todayDate();
      setMonth(date.slice(0, 7)); setOpen(true);
    }}><CalendarDays size={18} /></button>
    {open && createPortal(<div ref={picker} id={pickerId} role="dialog" aria-label={`Choose date${props['aria-label'] ? ` for ${props['aria-label']}` : ''}`} className="bg-bg-panel border border-border rounded-lg shadow-xl text-white" style={{ position: 'fixed', zIndex: 10000, ...position, overflowY: 'auto', padding: 10 }} onClick={(event) => event.stopPropagation()}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <button type="button" aria-label="Previous month" onClick={() => navigateMonth(-1)} disabled={year === 1 && monthIndex === 0}><ChevronLeft size={20} /></button>
        <span aria-live="polite" className="text-sm font-medium">{MONTHS[monthIndex]} {year}</span>
        <button type="button" aria-label="Next month" onClick={() => navigateMonth(1)} disabled={year === 9999 && monthIndex === 11}><ChevronRight size={20} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 2 }}>
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => <span key={day} className="text-xs text-muted text-center">{day}</span>)}
        {Array.from({ length: (first.getUTCDay() + 6) % 7 }, (_, index) => <span key={`blank-${index}`} />)}
        {Array.from({ length: last.getUTCDate() }, (_, index) => {
          const iso = `${month}-${String(index + 1).padStart(2, '0')}`;
          return <button key={iso} type="button" data-calendar-day={iso} aria-label={dateInputText(iso)} aria-pressed={selected === iso} disabled={!allowed(iso)} onClick={() => choose(iso)} className={`rounded text-xs hover:bg-accent/20 disabled:opacity-30 ${selected === iso ? 'bg-accent text-white' : ''}`} style={{ height: 30 }}>{index + 1}</button>;
        })}
      </div>
      <div className="flex justify-between mt-2 text-xs">
        <button type="button" disabled={!allowed(todayDate())} onClick={() => choose(todayDate())} className="px-2 py-1 disabled:opacity-30">Today</button>
        <button type="button" onClick={() => choose('')} className="px-2 py-1">Clear</button>
      </div>
    </div>, document.body)}
  </span>;
}
